import styled from 'styled-components';
import React, { useContext, useMemo } from 'react';
import { useCatalogSearch } from '@/components/catalog-search/use-catalog-search';
import { CircularCheckmark } from '@/components/circular-checkmark';
import {
  formatMoneyWithLocale,
  getCurrencySymbol,
  PriceTestContext,
} from '@/features/editor/widgets/price-test/context';
import { PTOffsetType } from '@/features/editor/widgets/price-test/props';
import { OffsetInput } from '@/features/editor/widgets/price-test/offset-input';

export function PriceTestStepMatchProducts() {
  const { props, setProps } = useContext(PriceTestContext);
  const { searchText, setSearchText, products } = useCatalogSearch();

  const mergedProducts: Array<ProductLineItem> = useMemo(() => {
    const out: ProductLineItem[] = [];

    if (!searchText && props?.manualProducts?.length > 0) {
      out.push(
        ...(props?.manualProducts.map(
          (p) =>
            ({
              id: p.id,
              handle: p.handle,
              title: p.title,
              image: p.image,
              price: p.price,
              compareAtPrice: p.price + 10,
              isSelected: true,
              offsetType: p.offsetType,
              offsetValue: p.priceOffset,
            } as ProductLineItem),
        ) || []),
      );
    }

    out.push(
      ...(products
        ?.filter((p) => !out.find((op) => p.id === op.id))
        .map((p) => {
          const found = props?.manualProducts?.find((mp) => mp.id === p.id);
          return {
            id: p.id,
            handle: p.handle,
            title: p.title,
            image: p.image?.src,
            price: p.price,
            compareAtPrice: p.price + 10,
            isSelected: !!found,
            offsetType: found?.offsetType || `fixed`,
            offsetValue: found?.priceOffset || 0,
          } as ProductLineItem;
        }) || []),
    );

    return out;
  }, [products, props?.manualProducts, searchText]);

  const onSearch = (e) => {
    setSearchText(e.target.value);
  };

  const onProductSelectionChanged = (
    product: ProductLineItem,
    selected: boolean,
  ) => {
    setProps((draft) => {
      draft.manualProducts = draft.manualProducts || [];
      const foundInProps = draft.manualProducts.find(
        (mp) => mp?.id === product?.id,
      );
      if (selected && !foundInProps) {
        draft.manualProducts.push({
          id: product.id,
          handle: product.handle,
          title: product.title,
          image: product.image,
          price: product.price,
          compareAtPrice: product.compareAtPrice,
          offsetType: product.offsetType,
          priceOffset: product.offsetValue,
        });
      }

      if (!selected && foundInProps) {
        draft.manualProducts = draft.manualProducts.filter(
          (mp) => mp.id !== product.id,
        );
      }
    });
  };

  const onProductOffsetChanged = (
    id: string,
    type: PTOffsetType,
    value: number,
  ) => {
    setProps((draft) => {
      const found = draft.manualProducts?.find((mp) => mp.id === id);
      if (found) {
        found.offsetType = type;
        found.priceOffset = value;
      }
    });
  };

  return (
    <Wrapper>
      <Search
        placeholder="Search for products"
        defaultValue={searchText}
        onChange={onSearch}
      />
      {mergedProducts &&
        mergedProducts.map((p) => (
          <ProductRow
            key={p.id}
            product={p}
            onChange={onProductSelectionChanged}
            onUpdate={onProductOffsetChanged}
          />
        ))}
    </Wrapper>
  );
}

interface ProductLineItem {
  id: string;
  handle: string;
  title: string;
  image: string;
  price: number;
  compareAtPrice: number;

  offsetType: PTOffsetType;
  offsetValue: number;
  isSelected: boolean;
}

function ProductRow({
  product,
  onChange,
  onUpdate,
}: {
  product: ProductLineItem;
  onChange: (p: ProductLineItem, selected: boolean) => void;
  onUpdate: (id: string, type: PTOffsetType, value: number) => void;
}) {
  const { currency } = useContext(PriceTestContext);
  const finalPrice = useMemo(() => {
    const v = product.offsetValue || 0;
    switch (product.offsetType) {
      case `fixed`:
        return product.price + v;
      case `percent`:
        return product.price * (1 + v / 100);
      case `final`:
        return v;
      default:
        return product.price;
    }
  }, [product]);

  const beforePrice = useMemo(
    () => formatMoneyWithLocale(product?.price, currency),
    [product, currency],
  );

  const afterPrice = useMemo(
    () => formatMoneyWithLocale(finalPrice, currency),
    [finalPrice, currency],
  );

  return (
    <ProductRowWrapper>
      <CircularCheckmark
        square
        size="2rem"
        selected={product.isSelected}
        onChange={(selected) => onChange(product, selected)}
      />
      <img src={product.image} alt={product.title} />
      <div className={`product-info ${product.isSelected ? `selected` : ``}`}>
        <div className="offset-type">
          <button
            type="button"
            onClick={() => onUpdate(product.id, `fixed`, product.offsetValue)}
            className={product.offsetType === `fixed` ? `selected` : ``}
          >
            Fixed Amount
          </button>
          <span>/</span>
          <button
            type="button"
            onClick={() => onUpdate(product.id, `percent`, product.offsetValue)}
            className={product.offsetType === `percent` ? `selected` : ``}
          >
            Percentage
          </button>
          <span>/</span>
          <button
            type="button"
            onClick={() => onUpdate(product.id, `final`, product.offsetValue)}
            className={product.offsetType === `final` ? `selected` : ``}
          >
            Final Price
          </button>
        </div>

        <div className="title-row">
          <div className="title">{product.title}</div>
          <OffsetInput
            mode={product.offsetType === `final` ? `absolute` : `delta`}
            prefix={
              product.offsetType === `percent`
                ? ``
                : getCurrencySymbol(currency)
            }
            suffix={product.offsetType === `percent` ? `%` : ``}
            value={product.offsetValue || 0}
            onChange={(number) =>
              onUpdate(product.id, product.offsetType, number)
            }
          />
        </div>

        <div className="prices-wrapper">
          {beforePrice !== afterPrice && (
            <span className="price old">{beforePrice}</span>
          )}
          <span className="price">{afterPrice}</span>
        </div>
      </div>
    </ProductRowWrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 2rem;

  max-height: 40rem;
  overflow-y: scroll;

  padding-top: 1rem;
  padding-bottom: 4rem;

  mask-image: linear-gradient(
    var(--mask-direction, to bottom),
    hsl(0 0% 0% / 0),
    hsl(0 0% 0% / 1) 5%,
    hsl(0 0% 0% / 1) 95%,
    hsl(0 0% 0% / 0)
  );
`;

const Search = styled.input`
  border-radius: 2rem;
  margin: 0 0.5rem;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.04);
  border: solid 0.5px rgba(151, 151, 151, 0.25);
  background-color: #fff;
  font-family: Inter, sans-serif;
  text-transform: uppercase;
  padding: 0.8rem 2rem;
  font-size: 1.3rem;

  &&:focus {
    border: solid 0.5px rgba(151, 151, 151, 0.25);
  }
`;

const ProductRowWrapper = styled.div`
  display: grid;
  grid-template-columns: 2rem 7rem 1fr;
  gap: 2rem;

  img {
    height: 100%;
    width: 100%;
    border-radius: 1.5rem;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.26);
  }

  .product-info {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 1rem;

    &:not(.selected) {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  .offset-type {
    color: #798794;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: flex-start;
    gap: 0.4rem;

    button:first-of-type {
      padding-left: 0;
    }

    button {
      cursor: pointer;
      appearance: none;
      border: none;
      background: none;
      outline: none;
      color: #798794;
      font-size: 1.1rem;
      letter-spacing: -0.3px;

      &.selected {
        text-decoration: underline;
      }
    }

    span {
      color: #798794;
      font-size: 1.1rem;
      letter-spacing: -0.3px;
    }
  }

  .title-row {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: start;
    gap: 1rem;
  }

  .title {
    font-size: 1.4rem;
    font-weight: 500;
    color: #000;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .prices-wrapper {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;

    .price {
      font-family: Inter, sans-serif;
      font-size: 1.1rem;
      font-weight: normal;
      letter-spacing: -0.3px;
      color: #475562;
      text-transform: uppercase;
    }

    .price.old {
      text-decoration: line-through;
    }
  }

  div[class*='offset-input'] {
    margin-right: 0.5rem;
  }
`;
